/**
 * Shared edge-function invocation helpers for the query layer.
 *
 * `invokeEdgeJson(path, body, opts?)` — POSTs to
 * `${supabaseUrl}/functions/v1/<path>`, attaches the bearer token (unless
 * `opts.auth === 'none'`), and returns the parsed JSON. Throws on non-2xx with
 * a message drawn from `body.error || body.reason || opts.errorMessage`.
 *
 * `invokeEdgeStream(path, body, opts?)` — same POST shape but returns the raw
 * `Response` so the caller can feed it into `parseSSEStream`. Forwards
 * `opts.signal` for abort control (stop-generating buttons). Throws on non-2xx
 * before streaming starts.
 *
 * Example:
 *   import { invokeEdgeJson, invokeEdgeStream } from './_edge'
 *   import { CHAT, VERIFY_SESSION } from './_endpoints'
 *
 *   const data = await invokeEdgeJson(VERIFY_SESSION, { session_id })
 *   const res  = await invokeEdgeStream(CHAT, payload, { signal })
 */
import { supabaseUrl, supabaseAnonKey } from '@/lib/supabase'
import { getValidSession } from './auth'
import { classifyResponseError, wrapFetchError } from './_errors'

async function buildHeaders(auth) {
  const headers = {
    'Content-Type': 'application/json',
    apikey: supabaseAnonKey,
  }
  if (auth !== 'none') {
    const { accessToken } = await getValidSession()
    headers.Authorization = `Bearer ${accessToken}`
  }
  return headers
}

/** Read the response body once and discriminate to a typed error
 *  (NetworkError / RateLimitError / AuthExpiredError / ServerError /
 *  generic Error). Callers can `instanceof`-check to render the right
 *  UI. Falls back to the caller's `errorMessage` when the body has no
 *  `error` / `message` field. */
async function buildResponseError(res, fallback) {
  const body = await res.json().catch(() => ({}))
  return classifyResponseError(res, body, fallback)
}

/**
 * POST a JSON body to an edge function and return the parsed JSON response.
 * @param {string} path - endpoint slug (see `_endpoints.js`)
 * @param {Object} [body] - JSON body. Omitted/empty → `{}`.
 * @param {Object} [opts]
 * @param {'required'|'none'} [opts.auth='required']
 * @param {string} [opts.errorMessage] - fallback when response lacks error/reason
 * @param {AbortSignal} [opts.signal] - forward to fetch for cancellation
 *   (e.g. Studio Plan tab cancels generate-outline when the user navigates
 *   away mid-flight, preventing stale-write bugs + wasted Anthropic spend).
 * @returns {Promise<any>}
 */
export async function invokeEdgeJson(path, body = {}, opts = {}) {
  const { auth = 'required', errorMessage, signal } = opts
  const headers = await buildHeaders(auth)
  let res
  try {
    res = await fetch(`${supabaseUrl}/functions/v1/${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    })
  } catch (err) {
    // AbortError stays as-is (caller signaled abort). fetch() throws a
    // TypeError on network-layer failure (offline, DNS, TLS, connection
    // reset). Wrap non-abort errors so callers can branch on NetworkError.
    if (err?.name === 'AbortError') throw err
    throw wrapFetchError(err)
  }
  if (!res.ok) {
    throw await buildResponseError(res, errorMessage)
  }
  return res.json()
}

/**
 * POST a JSON body to an edge function and return the raw `Response` for SSE
 * streaming via `parseSSEStream`. Abortable via `opts.signal`.
 * @param {string} path - endpoint slug (see `_endpoints.js`)
 * @param {Object} [body]
 * @param {Object} [opts]
 * @param {'required'|'none'} [opts.auth='required']
 * @param {AbortSignal} [opts.signal]
 * @param {string} [opts.errorMessage]
 * @returns {Promise<Response>}
 */
export async function invokeEdgeStream(path, body = {}, opts = {}) {
  const { auth = 'required', signal, errorMessage } = opts
  const headers = await buildHeaders(auth)
  let res
  try {
    res = await fetch(`${supabaseUrl}/functions/v1/${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    })
  } catch (err) {
    // AbortError stays as-is (caller signaled abort). Other fetch errors
    // wrap into NetworkError for typed branching.
    if (err?.name === 'AbortError') throw err
    throw wrapFetchError(err)
  }
  if (!res.ok) {
    throw await buildResponseError(res, errorMessage)
  }
  return res
}
